import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase.js';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if Firebase is configured
    const isFirebaseConfigured = () => {
        return process.env.REACT_APP_FIREBASE_API_KEY &&
            process.env.REACT_APP_FIREBASE_API_KEY !== 'undefined';
    };

    const signup = (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const logout = () => {
        return signOut(auth);
    };

    const loginWithGoogle = () => {
        const provider = new GoogleAuthProvider();
        return signInWithPopup(auth, provider);
    };

    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    };

    useEffect(() => {
        if (!isFirebaseConfigured()) {
            setError('Firebase not configured. Please see FIREBASE_SETUP.md');
            setLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        signup,
        login,
        logout,
        loginWithGoogle,
        resetPassword
    };

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl p-8">
                    <h1 className="text-3xl font-bold text-red-600 mb-4">⚠️ Firebase Not Configured</h1>
                    <p className="text-slate-700 mb-4">
                        Firebase credentials are missing. Please follow these steps:
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-slate-600 mb-6">
                        <li>Open <code className="bg-slate-100 px-2 py-1 rounded">FIREBASE_SETUP.md</code> in your project</li>
                        <li>Create a Firebase project (free)</li>
                        <li>Copy <code className="bg-slate-100 px-2 py-1 rounded">.env.example</code> to <code className="bg-slate-100 px-2 py-1 rounded">.env.local</code></li>
                        <li>Add your Firebase credentials to <code className="bg-slate-100 px-2 py-1 rounded">.env.local</code></li>
                        <li>Restart the app with <code className="bg-slate-100 px-2 py-1 rounded">npm start</code></li>
                    </ol>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> The app will work without Firebase, but you won't have user authentication or cloud storage.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
